import { AppError } from "../../lib/appError";
import { config } from "../../lib/config";
import type { Json } from "../../lib/database.types";
import {
  normalizeSubdomainInput,
  RESERVED_SUBDOMAINS,
  SUBDOMAIN_PATTERN,
} from "../../lib/hostname";
import {
  DEFAULT_SITE_THEME,
  withSiteDesign,
  type SiteDesign,
  type SiteThemeId,
} from "../themes/siteThemes";
import { siteRepository } from "./siteRepository";

export type SiteTemplate = "portfolio" | "photography" | "personal" | "apps" | "video";
/** How onboarding starts a site: from its example, or blank. */
export type SiteStart = "example" | "blank";

export const siteService = {
  list: () => siteRepository.list(),
  create: (input: {
    name: string;
    description?: string | null;
    subdomain?: string | null;
  }) =>
    siteRepository.create(
      input.name.trim(),
      input.description?.trim() || null,
      input.subdomain?.trim() || null,
    ),

  /**
   * A new site of the chosen kind: the example site for it (sample pages,
   * work and photographs to replace), or a blank one with only a Home page.
   */
  async createFromTemplate(input: {
    template: SiteTemplate;
    start?: SiteStart;
    theme?: SiteThemeId;
    name: string;
    description?: string | null;
  }) {
    const create =
      input.start === "blank"
        ? siteRepository.createBlank
        : siteRepository.createFromTemplate;
    const site = await create(
      input.template,
      input.name.trim(),
      input.description?.trim() || null,
    );
    if (input.theme && input.theme !== DEFAULT_SITE_THEME && site.draft_version_id) {
      // The site already exists, so a failed theme save must not fail
      // onboarding (a retry would create a second site). It stays changeable
      // in Design.
      await siteService
        .updateDesign(site.draft_version_id, { theme: input.theme })
        .catch(() => undefined);
    }
    return site;
  },

  /** The id of the site's current draft; it changes whenever the site is published. */
  async draftVersionId(siteId: string) {
    return (await siteRepository.get(siteId)).draft_version_id;
  },

  async getEditorSite(siteId: string) {
    const site = await siteRepository.get(siteId);
    if (!site.draft_version_id)
      throw new AppError("This website has no editable draft.", "NO_DRAFT");
    const draft = await siteRepository.getVersion(site.draft_version_id);
    const published = site.published_version_id
      ? await siteRepository.getVersion(site.published_version_id)
      : null;
    return { site, draft, published };
  },

  async updateSubdomain(siteId: string, value: string | null) {
    const candidate = normalizeSubdomainInput(
      value ?? "",
      config.hazumuRootDomain,
    );
    if (!candidate) return siteRepository.updateSubdomain(siteId, null);
    if (!SUBDOMAIN_PATTERN.test(candidate)) {
      throw new AppError(
        "Use only lowercase letters, numbers and hyphens — like my-site.",
        "SUBDOMAIN_INVALID",
      );
    }
    if (RESERVED_SUBDOMAINS.has(candidate)) {
      throw new AppError("That address is reserved.", "SUBDOMAIN_RESERVED");
    }
    try {
      return await siteRepository.updateSubdomain(siteId, candidate);
    } catch (saveError) {
      if (saveError instanceof AppError && saveError.code === "23505") {
        throw new AppError(
          "That address is already taken.",
          "SUBDOMAIN_TAKEN",
          saveError,
        );
      }
      throw saveError;
    }
  },

  updateDraft: (
    versionId: string,
    values: { name?: string; description?: string | null; settings?: Json },
  ) =>
    siteRepository.updateDraft(versionId, {
      ...values,
      ...(values.name === undefined ? {} : { name: values.name.trim() }),
      ...(values.description === undefined
        ? {}
        : { description: values.description?.trim() || null }),
    }),

  /** Merges into the draft's current settings so other keys are kept. */
  async updateDesign(versionId: string, patch: Partial<SiteDesign>) {
    const draft = await siteRepository.getVersion(versionId);
    return siteRepository.updateDraft(versionId, {
      settings: withSiteDesign(draft.settings, patch),
    });
  },

  publish: (siteId: string) => siteRepository.publish(siteId),
};
